import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  AssignHousekeepingRoomAPI,
  DeleteHousekeepingRoomNoteAPI,
  GetHousekeepersAPI,
  GetHousekeepingCountersAPI,
  GetHousekeepingPieAPI,
  GetHousekeepingRoomDetailsAPI,
  GetHousekeepingRoomsAPI,
  RemoveHousekeepingRoomAssignmentAPI,
  RetryHousekeepingJobAPI,
  SaveHousekeepingRoomNoteAPI,
  SelfAssignHousekeepingRoomAPI,
  UpdateHousekeepingRoomStatusAPI,
} from "api/housekeeping";

export const getHousekeepingRooms = createAsyncThunk(
  "housekeeping/getHousekeepingRooms",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepingRoomsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch housekeeping rooms"
      );
    }
  }
);

export const getHousekeepingRoomDetails = createAsyncThunk(
  "housekeeping/getHousekeepingRoomDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepingRoomDetailsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch room details"
      );
    }
  }
);

export const getHousekeepingCounters = createAsyncThunk(
  "housekeeping/getHousekeepingCounters",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepingCountersAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch housekeeping counters"
      );
    }
  }
);

export const getHousekeepingPie = createAsyncThunk(
  "housekeeping/getHousekeepingPie",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepingPieAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch housekeeping statistics"
      );
    }
  }
);

export const getHousekeepers = createAsyncThunk(
  "housekeeping/getHousekeepers",
  async (hotelId, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepersAPI(hotelId);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch housekeepers"
      );
    }
  }
);

export const updateHousekeepingRoomStatus = createAsyncThunk(
  "housekeeping/updateHousekeepingRoomStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await UpdateHousekeepingRoomStatusAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to update room status"
      );
    }
  }
);

export const assignHousekeepingRoom = createAsyncThunk(
  "housekeeping/assignHousekeepingRoom",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AssignHousekeepingRoomAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to assign room"
      );
    }
  }
);

export const removeHousekeepingRoomAssignment = createAsyncThunk(
  "housekeeping/removeHousekeepingRoomAssignment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await RemoveHousekeepingRoomAssignmentAPI(payload);

      return {
        ...response?.data?.data,
        roomId: payload.roomId,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to remove room assignment"
      );
    }
  }
);

export const selfAssignHousekeepingRoom = createAsyncThunk(
  "housekeeping/selfAssignHousekeepingRoom",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await SelfAssignHousekeepingRoomAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to claim room"
      );
    }
  }
);

export const saveHousekeepingRoomNote = createAsyncThunk(
  "housekeeping/saveHousekeepingRoomNote",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await SaveHousekeepingRoomNoteAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to save room note"
      );
    }
  }
);

export const deleteHousekeepingRoomNote = createAsyncThunk(
  "housekeeping/deleteHousekeepingRoomNote",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DeleteHousekeepingRoomNoteAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to delete room note"
      );
    }
  }
);

export const retryHousekeepingJob = createAsyncThunk(
  "housekeeping/retryHousekeepingJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await RetryHousekeepingJobAPI(jobId);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to retry housekeeping job"
      );
    }
  }
);

/* =========================================================
   INITIAL STATE
========================================================= */

const initialState = {
  rooms: [],
  page: 1,
  pageSize: 20,
  total: 0,

  roomsLoading: false,
  roomsError: null,
  roomsSuccess: false,

  roomDetails: null,
  roomDetailsLoading: false,
  roomDetailsError: null,

  counters: {
    cleanedRooms: 0,
    dirtyRooms: 0,
    vacantRooms: 0,
    reserved: {
      reserved: 0,
      total: 0,
    },
  },
  countersLoading: false,
  countersError: null,

  pie: {
    clean: 0,
    dirty: 0,
    total: 0,
    cleanedPercentage: 0,
  },
  pieLoading: false,
  pieError: null,

  housekeepers: [],
  housekeepersLoading: false,
  housekeepersError: null,

  updateStatusLoading: false,
  updateStatusError: null,
  updateStatusSuccess: false,

  assignRoomLoading: false,
  assignRoomError: null,
  assignRoomSuccess: false,

  removeAssignmentLoading: false,
  removeAssignmentError: null,
  removeAssignmentSuccess: false,

  selfAssignLoading: false,
  selfAssignError: null,
  selfAssignSuccess: false,

  saveNoteLoading: false,
  saveNoteError: null,
  saveNoteSuccess: false,

  deleteNoteLoading: false,
  deleteNoteError: null,
  deleteNoteSuccess: false,

  retryJobLoading: false,
  retryJobError: null,
  retryJobSuccess: false,
  retryJobId: null,
};

/* =========================================================
   SLICE
========================================================= */

const housekeepingSlice = createSlice({
  name: "housekeeping",

  initialState,

  reducers: {
    resetHousekeepingRoomsState: (state) => {
      state.roomsLoading = false;
      state.roomsError = null;
      state.roomsSuccess = false;
    },

    clearHousekeepingRooms: (state) => {
      state.rooms = [];
      state.page = 1;
      state.pageSize = 20;
      state.total = 0;
      state.roomsError = null;
    },

    resetRoomDetailsState: (state) => {
      state.roomDetails = null;
      state.roomDetailsLoading = false;
      state.roomDetailsError = null;
    },

    resetUpdateStatusState: (state) => {
      state.updateStatusLoading = false;
      state.updateStatusError = null;
      state.updateStatusSuccess = false;
    },

    resetAssignRoomState: (state) => {
      state.assignRoomLoading = false;
      state.assignRoomError = null;
      state.assignRoomSuccess = false;
    },

    resetRemoveAssignmentState: (state) => {
      state.removeAssignmentLoading = false;
      state.removeAssignmentError = null;
      state.removeAssignmentSuccess = false;
    },

    resetSelfAssignState: (state) => {
      state.selfAssignLoading = false;
      state.selfAssignError = null;
      state.selfAssignSuccess = false;
    },

    resetSaveNoteState: (state) => {
      state.saveNoteLoading = false;
      state.saveNoteError = null;
      state.saveNoteSuccess = false;
    },

    resetDeleteNoteState: (state) => {
      state.deleteNoteLoading = false;
      state.deleteNoteError = null;
      state.deleteNoteSuccess = false;
    },

    resetRetryJobState: (state) => {
      state.retryJobLoading = false;
      state.retryJobError = null;
      state.retryJobSuccess = false;
      state.retryJobId = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===========================
      // ROOM LIST
      // ===========================

      .addCase(getHousekeepingRooms.pending, (state) => {
        state.roomsLoading = true;
        state.roomsError = null;
        state.roomsSuccess = false;
      })

      .addCase(getHousekeepingRooms.fulfilled, (state, action) => {
        state.roomsLoading = false;
        state.roomsSuccess = true;

        const items = action.payload?.items || [];
        const page = action.payload?.page || 1;

        if (page === 1) {
          state.rooms = items;
        } else {
          const existingIds = new Set(state.rooms.map((room) => room.id));

          state.rooms = [...state.rooms, ...items.filter((room) => !existingIds.has(room.id))];
        }

        state.page = page;
        state.pageSize = action.payload?.pageSize || 20;
        state.total = action.payload?.total || 0;
      })

      .addCase(getHousekeepingRooms.rejected, (state, action) => {
        state.roomsLoading = false;
        state.roomsError = action.payload;
      })

      // ===========================
      // ROOM DETAILS
      // ===========================

      .addCase(getHousekeepingRoomDetails.pending, (state) => {
        state.roomDetailsLoading = true;
        state.roomDetailsError = null;
      })

      .addCase(getHousekeepingRoomDetails.fulfilled, (state, action) => {
        state.roomDetailsLoading = false;
        state.roomDetails = action.payload || null;
      })

      .addCase(getHousekeepingRoomDetails.rejected, (state, action) => {
        state.roomDetailsLoading = false;
        state.roomDetailsError = action.payload;
      })

      // ===========================
      // COUNTERS
      // ===========================

      .addCase(getHousekeepingCounters.pending, (state) => {
        state.countersLoading = true;
        state.countersError = null;
      })

      .addCase(getHousekeepingCounters.fulfilled, (state, action) => {
        state.countersLoading = false;

        state.counters = action.payload || {
          cleanedRooms: 0,
          dirtyRooms: 0,
          vacantRooms: 0,
          reserved: {
            reserved: 0,
            total: 0,
          },
        };
      })

      .addCase(getHousekeepingCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersError = action.payload;
      })

      // ===========================
      // PIE / COMPLETION
      // ===========================

      .addCase(getHousekeepingPie.pending, (state) => {
        state.pieLoading = true;
        state.pieError = null;
      })

      .addCase(getHousekeepingPie.fulfilled, (state, action) => {
        state.pieLoading = false;

        state.pie = action.payload || {
          dirty: 0,
          total: 0,
          dirtyPct: 0,
        };
      })

      .addCase(getHousekeepingPie.rejected, (state, action) => {
        state.pieLoading = false;
        state.pieError = action.payload;
      })

      // ===========================
      // HOUSEKEEPERS
      // ===========================

      .addCase(getHousekeepers.pending, (state) => {
        state.housekeepersLoading = true;
        state.housekeepersError = null;
      })

      .addCase(getHousekeepers.fulfilled, (state, action) => {
        state.housekeepersLoading = false;
        state.housekeepers = action.payload || [];
      })

      .addCase(getHousekeepers.rejected, (state, action) => {
        state.housekeepersLoading = false;
        state.housekeepersError = action.payload;
      })

      // ===========================
      // UPDATE STATUS
      // ===========================

      .addCase(updateHousekeepingRoomStatus.pending, (state) => {
        state.updateStatusLoading = true;
        state.updateStatusError = null;
        state.updateStatusSuccess = false;
      })

      .addCase(updateHousekeepingRoomStatus.fulfilled, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusSuccess = true;

        const updatedRoom = action.payload;

        const room = state.rooms.find((item) => item.id === updatedRoom?.id);

        if (room) {
          room.hkStatus = updatedRoom.hkStatus;
          room.version = updatedRoom.version;
        }

        if (state.roomDetails?.id === updatedRoom?.id) {
          state.roomDetails.roomOverview.hkStatus = updatedRoom.hkStatus;

          state.roomDetails.version = updatedRoom.version;
        }
      })

      .addCase(updateHousekeepingRoomStatus.rejected, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusError = action.payload;
      })

      // ===========================
      // ASSIGN ROOM
      // ===========================

      .addCase(assignHousekeepingRoom.pending, (state) => {
        state.assignRoomLoading = true;
        state.assignRoomError = null;
        state.assignRoomSuccess = false;
      })

      .addCase(assignHousekeepingRoom.fulfilled, (state, action) => {
        state.assignRoomLoading = false;
        state.assignRoomSuccess = true;

        const updatedRoom = action.payload;

        const room = state.rooms.find((item) => item.id === updatedRoom?.id);

        if (room) {
          room.assignedUserId = updatedRoom.assignedUserId;
          room.assignedUserName = updatedRoom.assignedUserName;
          room.version = updatedRoom.version;
        }

        if (state.roomDetails?.id === updatedRoom?.id) {
          if (!state.roomDetails.roomOverview) {
            state.roomDetails.roomOverview = {};
          }

          state.roomDetails.roomOverview.assignedUserId = updatedRoom.assignedUserId;

          state.roomDetails.version = updatedRoom.version;
        }
      })

      .addCase(assignHousekeepingRoom.rejected, (state, action) => {
        state.assignRoomLoading = false;
        state.assignRoomError = action.payload;
      })

      // ===========================
      // REMOVE ASSIGNMENT
      // ===========================

      .addCase(removeHousekeepingRoomAssignment.pending, (state) => {
        state.removeAssignmentLoading = true;
        state.removeAssignmentError = null;
        state.removeAssignmentSuccess = false;
      })

      .addCase(removeHousekeepingRoomAssignment.fulfilled, (state, action) => {
        state.removeAssignmentLoading = false;
        state.removeAssignmentSuccess = true;

        const roomId = action.payload?.id || action.payload?.roomId;

        const room = state.rooms.find((item) => item.id === roomId);

        if (room) {
          room.assignedUserId = null;
          room.assignedUserName = null;
          room.version = action.payload?.version;
        }

        if (state.roomDetails?.id === roomId) {
          if (!state.roomDetails.roomOverview) {
            state.roomDetails.roomOverview = {};
          }

          state.roomDetails.roomOverview.assignedUserId = null;

          state.roomDetails.version = action.payload?.version;
        }
      })

      .addCase(removeHousekeepingRoomAssignment.rejected, (state, action) => {
        state.removeAssignmentLoading = false;
        state.removeAssignmentError = action.payload;
      })

      // ===========================
      // SELF ASSIGN
      // ===========================

      .addCase(selfAssignHousekeepingRoom.pending, (state) => {
        state.selfAssignLoading = true;
        state.selfAssignError = null;
        state.selfAssignSuccess = false;
      })

      .addCase(selfAssignHousekeepingRoom.fulfilled, (state, action) => {
        state.selfAssignLoading = false;
        state.selfAssignSuccess = true;

        const updatedRoom = action.payload;

        const room = state.rooms.find((item) => item.id === updatedRoom?.id);

        if (room) {
          room.assignedUserId = updatedRoom.assignedUserId;

          room.version = updatedRoom.version;
        }

        if (state.roomDetails?.id === updatedRoom?.id) {
          if (!state.roomDetails.roomOverview) {
            state.roomDetails.roomOverview = {};
          }

          state.roomDetails.roomOverview.assignedUserId = updatedRoom.assignedUserId;

          state.roomDetails.version = updatedRoom.version;
        }
      })

      .addCase(selfAssignHousekeepingRoom.rejected, (state, action) => {
        state.selfAssignLoading = false;
        state.selfAssignError = action.payload;
      })

      // ===========================
      // SAVE NOTE
      // ===========================

      .addCase(saveHousekeepingRoomNote.pending, (state) => {
        state.saveNoteLoading = true;
        state.saveNoteError = null;
        state.saveNoteSuccess = false;
      })

      .addCase(saveHousekeepingRoomNote.fulfilled, (state, action) => {
        state.saveNoteLoading = false;
        state.saveNoteSuccess = true;

        const updated = action.payload;

        const room = state.rooms.find((item) => item.id === updated?.id);

        if (room) {
          room.noteText = updated?.note?.text || "";
          room.noteCount = updated?.note?.text ? 1 : 0;
          room.noteUpdatedAt = updated?.note?.updatedAt || updated?.note?.createdAt || null;
        }

        if (state.roomDetails?.id === updated?.id) {
          state.roomDetails.note = updated?.note || null;
        }
      })

      .addCase(saveHousekeepingRoomNote.rejected, (state, action) => {
        state.saveNoteLoading = false;
        state.saveNoteError = action.payload;
      })

      // ===========================
      // DELETE NOTE
      // ===========================

      .addCase(deleteHousekeepingRoomNote.pending, (state) => {
        state.deleteNoteLoading = true;
        state.deleteNoteError = null;
        state.deleteNoteSuccess = false;
      })

      .addCase(deleteHousekeepingRoomNote.fulfilled, (state, action) => {
        state.deleteNoteLoading = false;
        state.deleteNoteSuccess = true;

        const updated = action.payload;

        const room = state.rooms.find((item) => item.id === updated?.id);

        if (room) {
          room.noteText = "";
          room.noteCount = 0;
          room.noteUpdatedAt = null;
        }

        if (state.roomDetails?.id === updated?.id) {
          state.roomDetails.note = updated?.note || null;
        }
      })

      .addCase(deleteHousekeepingRoomNote.rejected, (state, action) => {
        state.deleteNoteLoading = false;
        state.deleteNoteError = action.payload;
      })

      // ===========================
      // RETRY JOB
      // ===========================

      .addCase(retryHousekeepingJob.pending, (state) => {
        state.retryJobLoading = true;
        state.retryJobError = null;
        state.retryJobSuccess = false;
        state.retryJobId = null;
      })

      .addCase(retryHousekeepingJob.fulfilled, (state, action) => {
        state.retryJobLoading = false;
        state.retryJobSuccess = true;
        state.retryJobId = action.payload?.id || null;
      })

      .addCase(retryHousekeepingJob.rejected, (state, action) => {
        state.retryJobLoading = false;
        state.retryJobError = action.payload;
      });
  },
});

export const {
  resetHousekeepingRoomsState,
  clearHousekeepingRooms,
  resetRoomDetailsState,
  resetUpdateStatusState,
  resetAssignRoomState,
  resetRemoveAssignmentState,
  resetSelfAssignState,
  resetSaveNoteState,
  resetDeleteNoteState,
  resetRetryJobState,
} = housekeepingSlice.actions;

export default housekeepingSlice.reducer;
